import { useEffect } from "react";

import { PolicyDocument, PolicySection } from "../components/PolicyDocument.js";
import { PLATFORM_PRIVACY_LAST_UPDATED } from "../lib/legal.js";

const SECTIONS = [
  { id: "scope", title: "Scope" },
  { id: "personal-information-we-collect", title: "Personal Information We Collect" },
  { id: "how-we-use-personal-information", title: "How We Use Personal Information" },
  {
    id: "how-we-may-disclose-personal-information",
    title: "How We May Disclose Personal Information",
  },
  { id: "cookies-and-other-tracking-mechanisms", title: "Cookies and Other Tracking Mechanisms" },
  { id: "third-party-links", title: "Third-Party Links" },
  { id: "user-generated-content", title: "User Generated Content" },
  { id: "your-privacy-choices", title: "Your Privacy Choices" },
  { id: "children", title: "Children" },
  { id: "security", title: "Security" },
  { id: "changes-to-this-policy", title: "Changes to This Policy" },
  { id: "contact-us", title: "Contact Us" },
] as const;

const SUPPORT_EMAIL = "support@boxed.gg";

/**
 * Loot Labs' own Privacy Policy for Loot OS, served at the storefront root's `/privacy`
 * (`lootos.com/privacy`), the one page on a host with no store. It is boxed.gg's policy
 * (`boxed-frontend/public/markdown/privacy.md`) copied with Loot Labs Inc. as the party, less the
 * two copy errors `PrivacyPage` also drops (a repeated pair of bullets under disclosure, "these
 * Terms" in the contact line). It renders outside the router and the tenant theme, so links are
 * plain anchors and the colors are the defaults.
 */
export function PlatformPrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy · Loot OS";
  }, []);

  return (
    <div className="min-h-svh bg-background px-4 py-8 text-foreground sm:py-12">
      <p className="mx-auto mb-8 max-w-3xl text-base font-semibold tracking-tight">Loot OS</p>
      <PolicyDocument title="Privacy Policy" lastUpdated={PLATFORM_PRIVACY_LAST_UPDATED}>
        <PolicySection id="scope" title="Scope">
          <p>
            This Privacy Policy (“Policy”) describes how Loot Labs Inc. and its affiliates,
            (collectively, “we,” “us” and “our”) collect, use, and disclose your personal
            information when you visit our websites (“Sites”) or use any of our products or services
            (collectively, “Services”) that link to this Policy. This Policy does not apply to the
            personal information that we collect and process about personnel or applicants. Your use
            of the Services is subject to this Policy and our Terms.
          </p>
          <ul>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ul>
        </PolicySection>

        <PolicySection id="personal-information-we-collect" title="Personal Information We Collect">
          <p>
            <strong>Personal Information We Collect From You.</strong> The personal information we
            collect from you depends upon how you use our Services or otherwise interact or engage
            with us, but generally includes:
          </p>
          <ul>
            <li>
              Identifiers and Contact Information. We may collect personal identifiers and contact
              information such as your name, address, email address, and phone number.
            </li>
            <li>
              Profile and Account Details. If you register an account with us, we collect your
              username, such as your email, user name, profile picture and the password used to
              login to your account.
            </li>
            <li>
              Commercial Information. We may also collect personal information about your purchasing
              patterns and history and use of our Site and Services.
            </li>
            <li>
              User and Message Contents. We may permit you to post or upload any content and
              feedback, including photos, videos, or other files, which is displayed publicly. In
              addition, when you contact our customer service team, including through our third
              party chat feature, texting or forms, we may collect your messages, emails, or any
              other personal information you choose to provide.
            </li>
            <li>
              Audio or Similar Information. If you speak with our customer service team by phone, we
              may collect a recording for quality assurance, security, and training purposes.
            </li>
            <li>
              Preferences. We may also collect personal information about the types of Services you
              use, your communications preferences, wish lists, and other preferences you may select
              in your account or profile.
            </li>
            <li>
              Other Information. We also collect the personal information you provide when you
              complete online forms, surveys, or leave us product reviews. We may also collect any
              other personal information you choose to provide.
            </li>
          </ul>
          <p>
            <strong>Personal Information We Collect Automatically.</strong> As described below in
            the “Cookies and Other Tracking Mechanisms” section, when you visit our Site or use our
            Services, we may automatically collect certain personal information, including:
          </p>
          <ul>
            <li>
              Identifiers and Device Information. We automatically collect IP address, unique
              identifiers, device name, type and model; browser type; operating system type, name,
              and version; information about your browser and browser language and other
              information.
            </li>
            <li>
              Network Activity. We may also collect information related to how you interact with our
              Site, Services and advertisements, such as page views, time and length of visit; links
              and items clicked; and other activity information, referring URL, browsing history,
              and other similar information. We may use analytics, advertising and other providers
              and technologies, including cookies and similar tools, to collect this information.
            </li>
          </ul>
          <p>
            <strong>Personal Information We Collect from Other Sources.</strong> We may collect
            personal information about you through our affiliates, services providers, marketing and
            advertising partners, public and other sources. Public Information. Personal information
            does not include publicly available information, such as information you post online or
            in our Services (such as your user name, profile picture or leaderboard place and
            activities) lawfully made available from government records, information we have a
            reasonable basis to believe is lawfully made available to the general public by you or
            widely distributed media, or by a person to whom you have disclosed the information and
            not restricted it to a specific audience, or deidentified or aggregated information. To
            the extent permitted by applicable law, we may combine the personal information we
            collect with publicly available or third-party sources.
          </p>
        </PolicySection>

        <PolicySection id="how-we-use-personal-information" title="How We Use Personal Information">
          <p>
            Generally, we may use the personal information we collect for the following purposes:
          </p>
          <ul>
            <li>
              Operate Our Business. We use the personal information we collect to run our day-to-day
              operations.
            </li>
            <li>
              Provide You Products and Services. We use the personal information we collect to
              provide you with our Site and Services, which may include referral and loyalty
              programs, sweepstakes, events and other activities, which may have different and/or
              additional terms and requirements. If you obtain a collectible associated with a video
              game, an NFT or physical item, you will need to provide us with information needed for
              fulfilment including game information, a blockchain wallet address or a physical
              shipping address.
            </li>
            <li>
              Communicate With You. We use the personal information we collect to communicate with
              you about your use of our Sites and Services, to respond to your inquiries, to provide
              you with the information you request, and for other customer service purposes.
            </li>
            <li>
              Evaluate and Improve Our Site and Services. We use the personal information we collect
              to evaluate, analyze, improve, and develop our products and Services, including this
              Site. We also use this information to tailor the content and information that we may
              send or display to you, to offer location customization, and to otherwise personalize
              your experiences while using our Site and Services.
            </li>
            <li>
              Marketing and Advertising. We may use your personal information for marketing and
              promotional purposes, including to send you promotional communications about products
              and services we believe may interest you. We may send communications via e-mail,
              regular mail or may send push notifications via a mobile device. We may also use your
              information to serve you advertisements or customized content online.
            </li>
            <li>
              Inferences. We may use the information we collect to better understand how you access
              and use our Site and Services, both on an aggregated and individualized basis.
            </li>
            <li>
              Fraud and Security Purposes. We may use the personal information we collect to
              mitigate fraud and protect ourselves and others. We also use this information to
              prevent and detect security issues, malicious or deceptive behavior or activities,
              infringements of our policies and contracts and other potential misuse of or illegal
              activity.
            </li>
            <li>
              Legal Requirements. We use the personal information we collect to investigate,
              enforce, and apply this Policy and our Terms, and to otherwise protect our own rights
              and interests, such as to resolve any disputes or to respond to requests from law
              enforcement, court orders, regulators, or other legal processes.
            </li>
            <li>
              Other Operational and Business Purposes. We may use your personal information for
              other operational purposes such as to comply with contracts related to products or
              Services that we provide to you.
            </li>
            <li>
              Other Uses. We may use your personal information for other purposes that we notify you
              of or obtain your consent.
            </li>
          </ul>
        </PolicySection>

        <PolicySection
          id="how-we-may-disclose-personal-information"
          title="How We May Disclose Personal Information"
        >
          <p>
            When permitted by applicable law, we may disclose your personal information to our
            affiliates, services providers, marketing and advertising partners, and others.
          </p>
          <ul>
            <li>
              Service Providers. We may disclose the personal information we collect with service
              providers who perform functions on our behalf, such as website hosting and analytics,
              email or other communication services, development and research, and customer support.
            </li>
            <li>
              Marketing and Advertising Partners. We may also disclose your personal information,
              such as name and contact information, with our marketing and advertising partners to
              send you content about certain products and Services.
            </li>
          </ul>
          <p>We also may disclose your personal information in the following circumstances:</p>
          <ul>
            <li>
              Business Transfers. We may disclose personal information as part of commercial
              transactions (e.g., mergers, acquisitions, bankruptcy, or other similar business
              transactions) and in contemplation of such transactions (e.g., due diligence).
            </li>
            <li>
              Legal Purposes. Personal information may be disclosed to third parties, as permitted
              by law or subpoena, or if we reasonably believe such action is necessary to comply
              with the law and the reasonable requests of regulators, law enforcement, other public
              authorities, or other similar purposes.
            </li>
            <li>
              Security and Protection of Rights. We also may share and/or use your information to
              protect the safety, rights, property, or security of Loot Labs Inc., our Site and
              Services, any third party, or the general public; to detect, prevent, or otherwise
              address fraud, security, cybersecurity, cybercrimes or technical issues; to prevent or
              stop any activity that we, in our sole discretion, may consider to be, or to pose a
              risk of being, illegal, unethical, or legally actionable; to use as evidence in
              litigation; to conduct audits; and/or to enforce our Terms.
            </li>
            <li>
              Aggregate and De-Identified Information. We may disclose aggregate or de-identified
              personal information about users with third parties for marketing, advertising,
              research and any other purposes.
            </li>
            <li>
              Other Disclosures. We may disclose your personal information for other purposes that
              we notify you of or obtain your consent.
            </li>
          </ul>
        </PolicySection>

        <PolicySection
          id="cookies-and-other-tracking-mechanisms"
          title="Cookies and Other Tracking Mechanisms"
        >
          <p>
            We and our third-party service providers use cookies, pixels, tags, and other similar
            tracking mechanisms to automatically collect personal information about browsing
            activity, type of device and similar information within our Services and to target
            advertising and content across our properties and third-party sites and services. We use
            this information to, for example, analyze and understand how users access, use, and
            interact with others, as well to identify and resolve bugs and errors and to assess,
            secure, protect, optimize and improve the performance of our Services.
          </p>
          <p>
            <strong>Cookies, Clear GIFs, Pixel Tags, and Other Technologies.</strong> “Cookies” are
            alphanumeric identifiers we transfer to your device through your browser for tracking,
            advertising, measurement, security and other purposes. Some cookies allow us to make it
            easier for you to navigate our Services, support the security and performance of the
            Services, or allow us to track activity and usage data within our Service. In addition
            to cookies, we may also use pixel tags (sometimes called web beacons or “clear GIFs”) to
            collect personal information about you and your use of our Services. While cookies are
            stored locally on your device, pixel tags are embedded invisibly within web pages and
            online content. We may use these, in connection with our Services to, among other
            things, track the activities of users, help us manage content and compile usage
            statistics. We may also use these in HTML e-mails we send, to help us track e-mail
            response rates, identify when our e-mails are viewed, and track whether our e-mails are
            forwarded.
          </p>
          <p>
            <strong>Third-Party Analytics and Tools.</strong> We use third party tools, such as
            Google Analytics, Mixpanel, and Meta Pixel, which are operated by third party companies.
            These third-party analytics companies may collect usage data (using cookies, pixels and
            similar tools) about our Site and Services in order to provide us with reports and
            metrics that help us evaluate usage and improve our Site and Services, and enhance
            performance and user experiences. These services record information such as purchase
            events, site navigation and profile changes. We may also use session replay cookies to
            understand your use of our Site and Services. To learn more about Google’s privacy
            practices, please review the Google Privacy Policy at{" "}
            <a href="https://www.google.com/policies/privacy/partners/">
              https://www.google.com/policies/privacy/partners/
            </a>
            . You can also download the Google Analytics Opt-out Browser Add-on to prevent your data
            from being used by Google Analytics at{" "}
            <a href="https://tools.google.com/dlpage/gaoptout">
              https://tools.google.com/dlpage/gaoptout
            </a>
            .
          </p>
          <p>
            We use Stripe for payment, analytics, and other business services. Stripe collects
            identifying information about the devices that connect to its services. Stripe uses this
            information to operate and improve the services it provides to us, including for fraud
            detection. You can learn more about Stripe and read its privacy policy at{" "}
            <a href="https://stripe.com/privacy">https://stripe.com/privacy</a>.
          </p>
          <p>
            <strong>Cross-Device Tracking.</strong> We and our third-party providers may use the
            personal information that we collect about you within our Services and on other
            third-party sites and services to help us and these third parties to identify other
            devices that you use (e.g., a mobile phone, tablet, other computer, etc.).
          </p>
          <p>
            <strong>Advertising, Personalized Content and Reporting.</strong> We may work with third
            parties, such as ad networks, channel partners, analytics and measurement services and
            others to personalize content, messages and ads within our Services and/or on third
            party sites such as Facebook and similar companies. We may share personal information
            with these organizations, and we and those third parties may use cookies, pixels, tags,
            and other tools to collect usage, viewing, chatting, event, purchase and browsing
            information within our Services such as IP address, cookies and unique IDs, and other
            identifiers, as well as browsing information. We and these organizations use this
            information to provide you more relevant ads and content and to evaluate the success of
            such ads and content.
          </p>
          <p>
            <strong>Social Plugins.</strong> We may use social plugins on our Sites. When you view a
            Site that contains a plugin, your browser will create a direct link to the third
            parties’ servers. If you are logged into your social media account, the social media
            platform will record the visit to your social media account. Please visit the social
            media platform’s privacy policy for more information.
          </p>
          <p>
            <strong>Do-Not-Track.</strong> Currently, our systems do not recognize browser
            “do-not-track” requests. You may, however, disable certain tracking as discussed in this
            section (e.g., by disabling cookies); you also may opt-out of targeted advertising by
            following the instructions above.
          </p>
        </PolicySection>

        <PolicySection id="third-party-links" title="Third-Party Links">
          <p>
            Our Site and Services may contain links to third-party websites. Any access to and use
            of such linked websites is not governed by this Policy, but instead is governed by the
            privacy policies of those third party websites. We are not responsible for the
            information practices of such third party websites.
          </p>
        </PolicySection>

        <PolicySection id="user-generated-content" title="User Generated Content">
          <p>
            We invite you to post content on our Site and Services, including your comments,
            pictures, and any other information that you would like to be available on our Site or
            Services and meets our acceptable use policies. If you post content to our Site or
            Services, all of the information that you post will be available to all visitors to our
            Site and Services. If you post your own content on our Site or Services, your posting
            may become public and may be forwarded to others. We cannot prevent such information
            from being used in a manner that may violate this Policy, the law, or your personal
            privacy. We reserve the right to remove any content, but we do not actively monitor our
            Site and Services at all times. Please contact us as described below with any concerns.
          </p>
        </PolicySection>

        <PolicySection id="your-privacy-choices" title="Your Privacy Choices">
          <p>We provide several ways for you to manage choices about your personal information.</p>
          <ul>
            <li>
              Cookie Settings. If you wish to prevent cookies and other technologies from tracking
              your activity on our Sites and Services or visits across multiple websites, you can
              set your browser to block certain cookies or notify you when a cookie is set; you can
              also delete cookies. The “Help” portion of the toolbar on most browsers will tell you
              how to prevent your device from accepting new cookies, how to have the browser notify
              you when you receive a new cookie, or how to delete cookies. Visitors to our Sites who
              disable cookies will be able to browse the Sites, but some features may not function.
            </li>
            <li>
              Industry Opt-Outs. You can control how participating third-party ad companies use the
              information that they collect about your visits to our websites and those of third
              parties. If you are in the U.S., you can obtain more information and opt out of
              receiving targeted ads from participating third-party ad networks at
              aboutads.info/choices (Digital Advertising Alliance). These preferences are browser
              and devices specific. You will need to set your preferences for each browser and
              device you use.
            </li>
            <li>
              Marketing Communications. You can opt out of receiving marketing texts and emails from
              us by texting STOP or using the unsubscribe feature in the emails. We may continue to
              send you transactional emails.
            </li>
            <li>
              Account Information. You may access, update, or correct your account and profile
              information at any time by logging in to your account and updating the information we
              maintain or by emailing us as described below.
            </li>
          </ul>
        </PolicySection>

        <PolicySection id="children" title="Children">
          <p>
            Our Services are not designed for children, and we do not knowingly collect personal
            information from children under the age of thirteen. If we learn that we have received
            personal information directly from a child, we will delete such information from our
            systems. If you are a parent or legal guardian and you believe we have collected your
            child’s personal information in violation of applicable law, please contact us using the
            contact information below.
          </p>
        </PolicySection>

        <PolicySection id="security" title="Security">
          <p>
            We have implemented safeguards designed to protect personal information. Please be aware
            that despite our efforts, no data security measures can guarantee security.
          </p>
        </PolicySection>

        <PolicySection id="changes-to-this-policy" title="Changes to This Policy">
          <p>
            This Privacy Policy is in effect as of the date noted at the top of the Privacy Policy.
            We may change this Privacy Policy from time to time. If we do, we will post the revised
            version here and change the “last updated date” (the date it applies from) at the top of
            the Privacy Policy. You should check here regularly for the most up-to-date version of
            the Privacy Policy.
          </p>
        </PolicySection>

        <PolicySection id="contact-us" title="Contact Us">
          <p>
            If you have any questions about this Policy or otherwise need to contact Loot Labs Inc.
            for any reason, you can reach us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        </PolicySection>
      </PolicyDocument>
    </div>
  );
}
